import React, { useEffect, useRef, useState } from 'react';
import { useImmer } from 'use-immer';
import { useLocation, useParams } from 'react-router';
import { CitizenLessonDeviationComponent } from './citizen-lesson-deviation-component';
import { ICitizenLessonDeviation, ICitizenLessonDeviationErrors } from '../../interfaces';
import { YesNo } from '../../../../components/radio-group/yes-no-component';
import { TimePickerValue } from '../../../../components/time-picker';
import { DatePickerValue } from '../../../../components/date-picker';
import { moduleClientFactory } from '../../../../api-service/module-client-factory';
import { AttendanceSheetStateDto, LessonStateDto } from '../../../../api-service/clients';
import { CitizenLessonDeviationRepetitionRow } from './citizen-lesson-deviation-repetitions';
import { combineDateAndTime, toStartOfDay } from '../../../../utilities';
import { validateCitizenLessonDeviation } from '../../validation';
import { usePostToTab } from '../../../search/hooks/usePostToTab';
import { REFRESH_CITIZEN_SCHEDULE, REFRESH_COURSE_LESSON_TABLE, toSchultzDate } from '../../utils';
import { useMetaTitle } from '../../../../hooks';
import { CircularProgressCentered } from '../../../../components/circular-progress-centered';

const attendanceClient = moduleClientFactory.createAttendanceClient();

export function CitizenLessonDeviation() {
  const { citizenLessonDeviationId } = useParams<{ citizenLessonDeviationId?: string }>();
  const { citizenId: citizenIdFromQuery } = useParams<{ citizenId?: string }>();

  const { search } = useLocation();
  const params = new URLSearchParams(search.toLocaleLowerCase());
  const lessonIdFromQuery = params.get('lessonid') || undefined;
  const courseEnrollmentIdFromQuery = params.get('courseenrollmentid') || undefined;

  const [deviation, setDeviation] = useImmer<ICitizenLessonDeviation>({
    lessonId: lessonIdFromQuery ?? '',
    citizenId: citizenIdFromQuery ?? '',
    courseEnrollmentId: courseEnrollmentIdFromQuery ?? '',
    isExemptFromLesson: false,
    startCheck: true,
    endCheck: true,
    minTime: new Date(),
    maxTime: new Date(),
    comment: '',
  });

  const [errors, setErrors] = useState<ICitizenLessonDeviationErrors>({});
  const [isSaving, setIsSaving] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [repetitionLessons, setRepetitionLessons] = useState<CitizenLessonDeviationRepetitionRow[]>([]);
  const [selectedRepetitionLessonIds, setSelectedRepetitionLessonIds] = useState<string[]>([]);
  const [isReadOnly, setIsReadOnly] = useState<boolean>(false);

  const selectedTimesRef = useRef<{ startTime: TimePickerValue; endTime: TimePickerValue }>({ startTime: undefined, endTime: undefined });
  selectedTimesRef.current = { startTime: deviation.startTime, endTime: deviation.endTime };

  useMetaTitle(citizenLessonDeviationId ? 'Redigér særplan' : 'Opret særplan');

  const postUpdate = usePostToTab(REFRESH_COURSE_LESSON_TABLE);
  const postScheduleUpdate = usePostToTab(REFRESH_CITIZEN_SCHEDULE);

  useEffect(() => {
    async function loadData() {
      setIsLoading(true);
      try {
        let resolvedLessonId = lessonIdFromQuery;
        let resolvedCitizenId = citizenIdFromQuery;
        let resolvedCourseEnrollmentId = courseEnrollmentIdFromQuery;
        let devStart: Date | undefined;
        let devEnd: Date | undefined;

        let deviationData = {};
        let lessonAndCourseData = {};
        let citizenData = {};

        if (citizenLessonDeviationId) {
          const { citizenLessonDeviationDto: loaded } = await attendanceClient.getCitizenLessonDeviationById({ citizenLessonDeviationId });
          if (loaded) {
            resolvedLessonId = loaded.lessonId;
            resolvedCitizenId = loaded.citizenId;
            resolvedCourseEnrollmentId = loaded.courseEnrollmentId;
            devStart = loaded.beginAt ? new Date(loaded.beginAt) : undefined;
            devEnd = loaded.endAt ? new Date(loaded.endAt) : undefined;

            deviationData = {
              citizenLessonDeviationId: loaded.citizenLessonDeviationId,
              lessonId: loaded.lessonId,
              citizenId: loaded.citizenId,
              courseEnrollmentId: loaded.courseEnrollmentId,
              isExemptFromLesson: loaded.isExemptFromLesson,
              startCheck: loaded.startCheck,
              endCheck: loaded.endCheck,
              comment: loaded.comment || '',
            };
          }
        }

        const [lessonRes, enrollmentLessonsRes] = await Promise.all([
          resolvedLessonId ? attendanceClient.getLessonById({ lessonId: resolvedLessonId }) : null,
          resolvedCitizenId ? attendanceClient.getCourseEnrollmentLessonsByCitizenId({ citizenId: resolvedCitizenId }) : null,
        ]);

        if (lessonRes?.lesson) {
          const { course } = await attendanceClient.getCourseById({ courseId: lessonRes.lesson.courseId });
          setIsReadOnly(isAttendanceSheetApproved(lessonRes.lesson.attendanceSheetState));
          const lessonStart = new Date(lessonRes.lesson.beginAt);
          const lessonEnd = new Date(lessonRes.lesson.endAt);
          const start = devStart ?? lessonStart;
          const end = devEnd ?? lessonEnd;

          lessonAndCourseData = {
            courseName: course?.name ?? '',
            startDate: lessonStart,
            startTime: { hours: start.getHours(), minutes: start.getMinutes() },
            endTime: { hours: end.getHours(), minutes: end.getMinutes() },
            minTime: lessonStart,
            maxTime: lessonEnd,
          };

          if (!citizenLessonDeviationId) {
            lessonAndCourseData = {
              ...lessonAndCourseData,
              startCheck: !!lessonRes.lesson.earliestArrivalOnTimeBeforeStart,
              endCheck: !!lessonRes.lesson.earliestDepartureOnTimeBeforeEnd,
            };
          }
        }

        if (enrollmentLessonsRes?.citizenName) {
          citizenData = { citizenName: enrollmentLessonsRes.citizenName };
        }

        if (enrollmentLessonsRes?.lessons) {
          const deviationByLessonId = new Map((enrollmentLessonsRes.deviations ?? []).filter(x => !x.isDeleted).map(x => [x.lessonId, x]));
          const repetitions = enrollmentLessonsRes.lessons
            .filter(
              lesson =>
                lesson.courseEnrollmentId === resolvedCourseEnrollmentId &&
                (lesson.lessonId === resolvedLessonId || (!isAttendanceSheetApproved(lesson.attendanceSheetState) && lesson.lessonState !== LessonStateDto.Cancelled))
            )
            .map(lesson => ({ ...lesson, deviation: deviationByLessonId.get(lesson.lessonId) }));
          setRepetitionLessons(repetitions);
          if (resolvedLessonId && repetitions.some(lesson => lesson.lessonId === resolvedLessonId)) {
            setSelectedRepetitionLessonIds([resolvedLessonId]);
          }
        }

        setDeviation(draft => {
          Object.assign(draft, deviationData, lessonAndCourseData, citizenData);
        });
      } catch (error) {
        console.error('Data load failed', error);
      } finally {
        setIsLoading(false);
      }
    }

    loadData();
  }, []);

  async function handleSaveClick() {
    try {
      setIsSaving(true);
      const { startDate, startTime, endTime, isExemptFromLesson, startCheck, endCheck, lessonId, citizenId, courseEnrollmentId, comment } = deviation;
      const beginAt = isExemptFromLesson ? undefined : combineDateAndTime(startDate, startTime);
      const endAt = isExemptFromLesson ? undefined : combineDateAndTime(startDate, endTime);

      if (citizenLessonDeviationId) {
        await attendanceClient.updateCitizenLessonDeviation({
          lessonDeviation: {
            citizenLessonDeviationId,
            lessonId,
            citizenId,
            courseEnrollmentId,
            beginAt,
            endAt,
            isExemptFromLesson,
            startCheck,
            endCheck,
            isDeleted: false,
            comment,
          },
        });
      } else {
        await attendanceClient.createCitizenLessonDeviation({
          lessonDeviation: {
            lessonId,
            citizenId,
            courseEnrollmentId,
            beginAt,
            endAt,
            isExemptFromLesson,
            startCheck,
            endCheck,
            comment,
          },
        });
      }

      const selectedRepetitions = repetitionLessons.filter(lesson => lesson.lessonId !== lessonId && selectedRepetitionLessonIds.includes(lesson.lessonId));
      await Promise.all(
        selectedRepetitions.map(lesson => {
          const lessonDate = toSchultzDate(lesson.beginAt);
          const repetitionBeginAt = isExemptFromLesson ? undefined : combineDateAndTime(lessonDate, startTime);
          const repetitionEndAt = isExemptFromLesson ? undefined : combineDateAndTime(lessonDate, endTime);

          if (lesson.deviation) {
            return attendanceClient.updateCitizenLessonDeviation({
              lessonDeviation: {
                citizenLessonDeviationId: lesson.deviation.citizenLessonDeviationId,
                lessonId: lesson.lessonId,
                citizenId,
                courseEnrollmentId: lesson.courseEnrollmentId,
                beginAt: repetitionBeginAt,
                endAt: repetitionEndAt,
                isExemptFromLesson,
                startCheck,
                endCheck,
                isDeleted: false,
                comment,
              },
            });
          }

          return attendanceClient.createCitizenLessonDeviation({
            lessonDeviation: {
              lessonId: lesson.lessonId,
              citizenId,
              courseEnrollmentId: lesson.courseEnrollmentId,
              beginAt: repetitionBeginAt,
              endAt: repetitionEndAt,
              isExemptFromLesson,
              startCheck,
              endCheck,
              comment,
            },
          });
        })
      );

      postUpdate(window.opener);
      postScheduleUpdate(window.opener);
      window.close();
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error(error);
    } finally {
      setIsSaving(false);
    }
  }

  function getSelectedDeviationIds() {
    const deviationIds = repetitionLessons
      .filter(lesson => selectedRepetitionLessonIds.includes(lesson.lessonId))
      .flatMap(lesson => (lesson.deviation ? [lesson.deviation.citizenLessonDeviationId] : []));

    if (citizenLessonDeviationId && !deviationIds.some(deviationId => deviationId.toLowerCase() === citizenLessonDeviationId.toLowerCase())) {
      deviationIds.push(citizenLessonDeviationId);
    }

    return deviationIds;
  }

  async function handleDelete() {
    try {
      setIsSaving(true);
      const deviationIds = getSelectedDeviationIds();
      await Promise.all(deviationIds.map(deviationId => attendanceClient.deleteCitizenLessonDeviation({ citizenLessonDeviationId: deviationId })));
      postUpdate(window.opener);
      postScheduleUpdate(window.opener);
      window.close();
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error(error);
    } finally {
      setIsSaving(false);
    }
  }

  function handleValidate() {
    const [isValid, tmpErrors] = validateCitizenLessonDeviation(deviation);
    setErrors({ ...tmpErrors });
    return isValid;
  }

  function handleYesNoChange(name: string, value: YesNo) {
    setDeviation((draft: any) => {
      draft[name] = value === YesNo.Yes;
    });
  }

  function handleTimeChange(name: string, value: TimePickerValue) {
    selectedTimesRef.current = { ...selectedTimesRef.current, [name]: value };
    setDeviation((draft: any) => {
      draft[name] = value;
    });
    setErrors({ ...errors, [name]: '' });
  }

  function handleCommentChange(value?: string) {
    setDeviation(draft => {
      draft.comment = value;
    });
  }

  function withCurrentLesson(lessonIds: string[]) {
    const currentLessonId = deviation.lessonId;
    if (currentLessonId && !lessonIds.includes(currentLessonId)) {
      return [currentLessonId, ...lessonIds];
    }
    return lessonIds;
  }

  function handleRepetitionSelectionChange(lessonIds: string[]) {
    setSelectedRepetitionLessonIds(withCurrentLesson(lessonIds));
  }

  function selectLessonsInSameTimeWindow(includeLesson: (lesson: CitizenLessonDeviationRepetitionRow) => boolean) {
    const { startTime, endTime } = selectedTimesRef.current;
    const matchingLessonIds = repetitionLessons
      .filter(lesson => {
        if (!includeLesson(lesson)) {
          return false;
        }
        const begin = lesson.deviation?.beginAt ?? lesson.beginAt;
        const end = lesson.deviation?.endAt ?? lesson.endAt;
        return matchesTime(toSchultzDate(begin), startTime) && matchesTime(toSchultzDate(end), endTime);
      })
      .map(lesson => lesson.lessonId);
    setSelectedRepetitionLessonIds(withCurrentLesson(matchingLessonIds));
  }

  function handleSelectSameTimeWindow() {
    selectLessonsInSameTimeWindow(() => true);
  }

  function handleSelectDeviationsInSameTimeWindow() {
    selectLessonsInSameTimeWindow(lesson => Boolean(lesson.deviation));
  }

  function handleSelectByWeekdaysInPeriod(weekdays: number[], fromDate: DatePickerValue, toDate: DatePickerValue) {
    const weekdaySet = new Set(weekdays);
    const from = fromDate ? toStartOfDay(toSchultzDate(fromDate)) : undefined;
    const to = toDate ? toStartOfDay(toSchultzDate(toDate)) : undefined;

    const matchingLessonIds = repetitionLessons
      .filter(lesson => {
        const lessonDate = toSchultzDate(lesson.beginAt);
        if (!weekdaySet.has(lessonDate.getDay())) {
          return false;
        }
        const lessonDay = toStartOfDay(lessonDate);
        if (from && lessonDay < from) {
          return false;
        }
        if (to && lessonDay > to) {
          return false;
        }
        return true;
      })
      .map(lesson => lesson.lessonId);
    setSelectedRepetitionLessonIds(withCurrentLesson(matchingLessonIds));
  }

  if (isLoading) {
    return <CircularProgressCentered />;
  }

  return (
    <CitizenLessonDeviationComponent
      isEditMode={!!citizenLessonDeviationId}
      isReadOnly={isReadOnly}
      isSaving={isSaving}
      deviation={deviation}
      errors={errors}
      repetitionLessons={repetitionLessons}
      selectedRepetitionLessonIds={selectedRepetitionLessonIds}
      deviationsToDeleteCount={getSelectedDeviationIds().length}
      currentLessonId={deviation.lessonId}
      onSaveClick={handleSaveClick}
      onDelete={handleDelete}
      onValidate={handleValidate}
      onYesNoChange={handleYesNoChange}
      onTimeChange={handleTimeChange}
      onCommentChange={handleCommentChange}
      onRepetitionSelectionChange={handleRepetitionSelectionChange}
      onSelectSameTimeWindow={handleSelectSameTimeWindow}
      onSelectDeviationsInSameTimeWindow={handleSelectDeviationsInSameTimeWindow}
      onSelectByWeekdaysInPeriod={handleSelectByWeekdaysInPeriod}
    />
  );
}

function isAttendanceSheetApproved(state?: AttendanceSheetStateDto) {
  return state === AttendanceSheetStateDto.Submitted;
}

function matchesTime(date: Date, time: TimePickerValue) {
  if (!time || typeof time === 'string') {
    return false;
  }
  return date.getHours() === time.hours && date.getMinutes() === time.minutes;
}
